// useRoomSecret.ts - hook for extracting roomSecret and accessToken
// from the URL fragment, then clearing it from the address bar.
//
// Security: the fragment (#k=...&a=...) is never sent to the server.
// We extract it on page load, store it in sessionStorage, and use
// history.replaceState to remove it from the visible URL.

import { useState, useEffect } from 'react';

export interface RoomCredentials {
  roomSecret: string;
  accessToken: string;
}

// Parse #k={roomSecret}&a={accessToken} from the URL fragment.
function parseFragment(): Partial<RoomCredentials> {
  const hash = window.location.hash.slice(1); // remove leading #
  const params = new URLSearchParams(hash);
  return {
    roomSecret: params.get('k') ?? undefined,
    accessToken: params.get('a') ?? undefined,
  };
}

// Extract the roomId from the path /r/{roomId}.
export function getRoomIdFromPath(): string | null {
  const match = window.location.pathname.match(/^\/r\/([^/]+)$/);
  return match ? match[1] : null;
}

// Clear the fragment from the URL without triggering a navigation.
function clearFragment() {
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

// useRoomSecret returns the room credentials from the URL fragment
// (on first load) or from sessionStorage (on subsequent loads after
// the fragment has been cleared).
export function useRoomSecret(): RoomCredentials | null {
  const [creds, setCreds] = useState<RoomCredentials | null>(null);

  useEffect(() => {
    // Try sessionStorage first (set on a previous load).
    const stored = sessionStorage.getItem('crossclip-room-creds');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as RoomCredentials;
        if (parsed.roomSecret && parsed.accessToken) {
          setCreds(parsed);
          clearFragment();
          return;
        }
      } catch {
        // Corrupt storage; fall through to fragment.
      }
    }

    // Otherwise parse from URL fragment.
    const frag = parseFragment();
    if (frag.roomSecret && frag.accessToken) {
      const c = { roomSecret: frag.roomSecret, accessToken: frag.accessToken };
      sessionStorage.setItem('crossclip-room-creds', JSON.stringify(c));
      setCreds(c);
      clearFragment();
    }
  }, []);

  return creds;
}

// Build the shareable URL for a room: /r/{roomId}#k={roomSecret}&a={accessToken}
export function buildRoomURL(roomId: string, roomSecret: string, accessToken: string): string {
  return `${window.location.origin}/r/${roomId}#k=${encodeURIComponent(roomSecret)}&a=${encodeURIComponent(accessToken)}`;
}
